import { useApi } from '@classgo/app-kit';
import { Badge, Button, Card, EmptyState, Table } from '@classgo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { staffQueries } from '../../api/staff-api';
import type { TenantView } from '../../api/types';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/format';

/**
 * Whole days from now to a `YYYY-MM-DD` date; negative when it passed.
 *
 * @param {string} date -- The ISO date.
 * @returns The day count.
 */
const daysUntil = (date: string): number => Math.ceil((Date.parse(date) - Date.now()) / 86_400_000);

/**
 * Every school, newest first. A row opens the school; the button starts the onboarding wizard.
 */
export const TenantsScreen = () => {
  const t = useT();
  const api = useApi();
  const navigate = useNavigate();
  const { data: tenants } = useSuspenseQuery(staffQueries.tenants(api));
  const newButton = (
    <Button onClick={() => void navigate({ to: '/colegios/nuevo' })}>{t('tenants.new')}</Button>
  );
  return (
    <Card title={t('tenants.title')} actions={newButton} flush>
      <Table<TenantView>
        caption={t('tenants.caption')}
        rows={tenants}
        rowKey={(row) => row.id}
        onRowClick={(row) =>
          void navigate({ to: '/colegios/$tenantId', params: { tenantId: row.id } })
        }
        empty={
          <EmptyState
            title={t('tenants.empty.title')}
            description={t('tenants.empty.body')}
            action={newButton}
          />
        }
        columns={[
          {
            key: 'name',
            header: t('tenants.column.name'),
            sortable: true,
            sortValue: (row) => row.name,
            cell: (row) => (
              <div>
                <p className="font-medium text-gray-900">{row.name}</p>
                {row.legalName ? <p className="text-xs text-gray-500">{row.legalName}</p> : null}
              </div>
            ),
          },
          {
            key: 'status',
            header: t('tenants.column.status'),
            cell: (row) => (
              <span className="flex flex-wrap gap-1">
                <Badge tone={row.status === 'active' ? 'success' : 'warning'}>
                  {row.status === 'active'
                    ? t('tenant.status.active')
                    : t('tenant.status.suspended')}
                </Badge>
                {row.isDemo ? <Badge tone="info">{t('tenant.demo')}</Badge> : null}
                {row.courtesyEndsOn && daysUntil(row.courtesyEndsOn) <= 30 ? (
                  <Badge tone={daysUntil(row.courtesyEndsOn) < 0 ? 'danger' : 'warning'}>
                    {daysUntil(row.courtesyEndsOn) < 0
                      ? t('tenant.courtesy.ended', { date: formatDate(row.courtesyEndsOn) })
                      : t('tenant.courtesy.ending', { days: daysUntil(row.courtesyEndsOn) })}
                  </Badge>
                ) : null}
              </span>
            ),
          },
          { key: 'country', header: t('tenants.column.country'), cell: (row) => row.countryCode },
          {
            key: 'created',
            header: t('tenants.column.created'),
            sortable: true,
            sortValue: (row) => row.createdAt,
            cell: (row) => formatDate(row.createdAt),
          },
        ]}
      />
    </Card>
  );
};
